import { type User } from '@supabase/supabase-js';
import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { supabase } from '../../lib/supabase';
import { getDb, resetLocalDatabase } from '../../db/client';
import { markOnboardingPending } from '../onboarding/onboardingFlag';
import { parseFragmentParams } from '../../ui/shared/parseFragmentParams';

WebBrowser.maybeCompleteAuthSession();

// Reexportado por compatibilidade: telas que consomem `useAuth()` recebem
// `supabase` de volta no objeto retornado (ver `return` abaixo), mas alguns
// pontos do código importavam esse client diretamente daqui.
export { supabase };

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      return data.user;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });
      if (error) throw error;
      if (data.user) {
        // Marca que o mini-onboarding (valor da hora + mark-up, spec S01)
        // deve aparecer na próxima vez que esta usuária estiver autenticada
        // — pode ser já nesta sessão ou só depois de confirmar o e-mail e
        // logar (o flag é persistido em AsyncStorage, sobrevive a isso). Só
        // roda em signUp, nunca em signIn: onboarding "uma vez após
        // cadastro", não em todo login.
        await markOnboardingPending(data.user.id);
      }
      return data.user;
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const redirectUrl = Linking.createURL('/');

      if (Platform.OS === 'web') {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: typeof window !== 'undefined' && window.location?.origin ? window.location.origin : redirectUrl,
          },
        });
        if (error) throw error;
        return data;
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: true,
        },
      });

      if (error) throw error;
      if (!data?.url) return null;

      const result = await WebBrowser.openAuthSessionAsync(data.url, redirectUrl);

      if (result.type === 'success' && result.url) {
        const params = parseFragmentParams(result.url);
        if (params.error) {
          throw new Error(params.error_description || params.error);
        }
        if (params.access_token && params.refresh_token) {
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
            access_token: params.access_token,
            refresh_token: params.refresh_token,
          });
          if (sessionError) throw sessionError;
          if (sessionData?.user) {
            const isNewUser =
              sessionData.user.created_at &&
              sessionData.user.last_sign_in_at &&
              Math.abs(new Date(sessionData.user.created_at).getTime() - new Date(sessionData.user.last_sign_in_at).getTime()) < 5000;
            if (isNewUser) {
              await markOnboardingPending(sessionData.user.id);
            }
          }
          return sessionData?.user ?? null;
        } else if (params.code) {
          const { data: codeData, error: codeError } = await supabase.auth.exchangeCodeForSession(params.code);
          if (codeError) throw codeError;
          if (codeData?.user) {
            const isNewUser =
              codeData.user.created_at &&
              codeData.user.last_sign_in_at &&
              Math.abs(new Date(codeData.user.created_at).getTime() - new Date(codeData.user.last_sign_in_at).getTime()) < 5000;
            if (isNewUser) {
              await markOnboardingPending(codeData.user.id);
            }
          }
          return codeData?.user ?? null;
        }
      }

      return null;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const passwordReset = async (email: string) => {
    try {
      // `redirectTo` é para onde o GoTrue manda a usuária de volta depois de
      // validar o link do e-mail — a tela `ResetPasswordScreen`
      // (`app/(auth)/reset-password.tsx`) lê a sessão/erro dessa URL.
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: Linking.createURL('/(auth)/reset-password'),
      });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  // Chamado por `ResetPasswordScreen` com o `access_token`/`refresh_token`
  // extraídos do deep link de recuperação (ver comentário no topo daquela
  // tela sobre por que é `setSession`, não `PASSWORD_RECOVERY`, nesta
  // versão/configuração do client). Isso dispara o listener acima e popula
  // `user` — é o que permite a tela mostrar o formulário de nova senha.
  const completePasswordRecovery = async (accessToken: string, refreshToken: string) => {
    try {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error establishing password recovery session:', error);
      throw error;
    }
  };

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  };

  const deleteAccount = async () => {
    try {
      const { error } = await supabase.functions.invoke('delete-account');
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }

    // A partir daqui o backend já confirmou a exclusão (Storage + tabelas +
    // usuário no Auth, ver supabase/functions/delete-account) — o que
    // resta é limpeza puramente local, e nenhuma falha aqui pode impedir o
    // signOut()/deixar a usuária "presa" logada localmente numa conta que
    // não existe mais no servidor. Por isso a limpeza do SQLite é
    // best-effort (captura e loga o erro em vez de propagar): se
    // resetLocalDatabase() falhar, ainda assim seguimos para signOut() —
    // um erro aqui vira dado órfão no device (ruim, mas não crítico e não
    // observável pela usuária, já que a conta some da UI de qualquer
    // forma), enquanto propagar faria a tela de exclusão mostrar "erro ao
    // excluir conta" para uma exclusão que na verdade já aconteceu.
    try {
      const db = await getDb();
      await resetLocalDatabase(db);
    } catch (error) {
      console.error('Error clearing local SQLite database after account deletion:', error);
    }

    await signOut();
  };

  return {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    passwordReset,
    completePasswordRecovery,
    updatePassword,
    deleteAccount,
    supabase
  };
}